/**
 * AI Chat API Service
 *
 * Communicates with the chat server's admin AI chat endpoints.
 * Auth: Uses admin JWT from sessionStorage as Bearer token.
 */

function getChatBaseUrl() {
  const override = localStorage.getItem('chatBaseUrl');
  if (override) return override;
  const env = localStorage.getItem('env') === 'true';
  return env
    ? 'https://prod-chat-module-api.dugoutlive.com'
    : 'https://chat-module-api.dugoutlive.com';
}

function getAuthHeaders() {
  const token = sessionStorage.getItem('sessionToken');
  if (!token) return {};
  return { Authorization: `Bearer ${token}`, auth: token };
}

async function handleResponse(res) {
  if (!res.ok) {
    let errorMsg = `Request failed: ${res.status}`;
    try { const body = await res.json(); errorMsg = body.message || body.error || errorMsg; } catch {}
    throw new Error(errorMsg);
  }
  return res.json();
}

/** Initial load — conversation + messages + suggestions */
export async function getAiConversation(matchId, sessionId = null) {
  let url = `${getChatBaseUrl()}/admin/ai-chat/${matchId}/conversation`;
  if (sessionId) url += `?session_id=${sessionId}`;
  const res = await fetch(url, { credentials: 'include', headers: getAuthHeaders() });
  return handleResponse(res);
}

/** Send a message */
export async function sendAiMessage(matchId, message, sessionId = null) {
  const body = { message };
  if (sessionId) body.session_id = sessionId;
  const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/${matchId}/message`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(body),
  });
  return handleResponse(res);
}

/** Paginated older messages */
export async function getAiMessages(conversationId, beforeTimestamp, limit = 20) {
  let url = `${getChatBaseUrl()}/admin/ai-chat/messages?conversationId=${conversationId}&limit=${limit}`;
  if (beforeTimestamp) url += `&beforeTimestamp=${beforeTimestamp}`;
  const res = await fetch(url, { credentials: 'include', headers: getAuthHeaders() });
  return handleResponse(res);
}

/** Like/dislike a message */
export async function reactToAiMessage(messageId, conversationId, reaction) {
  const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/messages/${messageId}/react`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ conversationId, reaction }),
  });
  return handleResponse(res);
}

/** Get suggested prompts for a match */
export async function getAiPrompts(matchId) {
  const res = await fetch(`${getChatBaseUrl()}/admin/ai-chat/${matchId}/prompts`, {
    credentials: 'include', headers: getAuthHeaders(),
  });
  return handleResponse(res);
}

/** Get chat history for a match (legacy) */
export async function getAiHistory(matchId, sessionId = null) {
  let url = `${getChatBaseUrl()}/admin/ai-chat/${matchId}/history`;
  if (sessionId) url += `?session_id=${sessionId}`;
  const res = await fetch(url, { credentials: 'include', headers: getAuthHeaders() });
  return handleResponse(res);
}

/** List all conversations */
export async function listAiConversations(limit = 20, offset = 0) {
  const res = await fetch(
    `${getChatBaseUrl()}/admin/ai-chat/conversations?limit=${limit}&offset=${offset}`,
    { credentials: 'include', headers: getAuthHeaders() }
  );
  return handleResponse(res);
}
